import React, { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Container, Grid, List, Message } from "semantic-ui-react";
import { orderDetailsSelector, orderPaySelector } from "../../utils/selectors";
import { PayPalButton } from "react-paypal-button-v2";
import {
  Body2,
  Heading5,
} from "../../components/value-cssmodules/TextComponent";
import styles from "./OrderSummary.module.scss";
import { useParams } from "react-router-dom";
import {
  getOrderDetails,
  payOrder,
  updateOrderStatus,
} from "../../actions/orderActions";
import Spinner from "../../components/Spinner";
import { CartData } from "../../types/siteTypes";
import OrderItem from "../PlaceOrder/OrderItem";
import isEmpty from "../../validation/is-empty";
import OrderCard from "../PlaceOrder/OrderCard";
import { FormattedMessage, useIntl } from "react-intl";
import { ORDER_PAY_RESET } from "../../actions/constants/order";
import dayjs from "dayjs";
import PrimaryButton from "../../components/Button/PrimaryButton";

const PayPalButtonAny: any = PayPalButton;

const OrderSummary = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const [sdkReady, setSdkReady] = useState(false);

  const dispatch = useDispatch();
  const intl = useIntl();
  const { order, loading, error } = useSelector((s) =>
    orderDetailsSelector(s)
  );
  const { loading: loadingPay, success: successPay } = useSelector((s) =>
    orderPaySelector(s)
  );

  useEffect(() => {
    const addPayPalScript = async () => {
      const { data: clientId } = await axios.get("/api/config/paypal");
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}`;
      script.async = true;
      script.onload = () => setSdkReady(true);
      document.body.appendChild(script);
    };

    if (!order || successPay) {
      dispatch({ type: ORDER_PAY_RESET });
    } else if (!order.isPaid && order.paymentMethod === "PayPal") {
      // @ts-ignore
      if (!window.paypal) {
        addPayPalScript();
      } else {
        setSdkReady(true);
      }
    }
  }, [orderId, dispatch, successPay, order]);

  useEffect(() => {
    if (orderId) {
      dispatch(getOrderDetails(orderId));
    }
  }, [dispatch, orderId]);

  if (loading) return <Spinner />;

  const successPaymentHandler = (paymentResult: any) => {
    if (orderId) {
      dispatch(payOrder(orderId, paymentResult));
    }
  };

  const cancelPaymentHandler = async () => {
    if (!orderId) return;

    dispatch(
      updateOrderStatus(orderId, {
        status: "CANCELED",
        reason: "Payment cancelled by user",
        provider: order?.paymentMethod || "PayPal",
        update_time: new Date().toISOString(),
      })
    );
  };

  const errorPaymentHandler = async (error: any) => {
    if (!orderId) return;

    const reason =
      typeof error?.message === "string" && error.message.trim()
        ? error.message
        : intl.formatMessage({
            id: "order.summary.paymentUnknown",
            defaultMessage: "Unknown payment error",
          });

    dispatch(
      updateOrderStatus(orderId, {
        status: "FAILED",
        reason,
        provider: order?.paymentMethod || "PayPal",
        update_time: new Date().toISOString(),
      })
    );
  };

  const handleStripeCheckout = async () => {
    try {
      const { data } = await axios.post(
        `/api/orders/${orderId}/stripe-session`,
        {},
        {
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
        }
      );

      window.location.href = data.url;
    } catch (err: any) {
      dispatch(
        updateOrderStatus(orderId!, {
          status: "FAILED",
          reason:
            err.message ||
            intl.formatMessage({
              id: "order.summary.stripeError",
              defaultMessage: "Stripe error",
            }),
          provider: "Stripe",
          update_time: new Date().toISOString(),
        })
      );
    }
  };

  return error ? (
    <Message warning>{error}</Message>
  ) : !isEmpty(order) ? (
    <Container>
      <Grid centered>
        <Grid.Column computer={10} mobile={16}>
          <Grid.Row className={styles.row}>
            <Heading5>
              <FormattedMessage
                id="order.summary.title"
                values={{ orderId: order._id }}
              />
            </Heading5>
            <Body2>
              <FormattedMessage id={"order.summary.paymentMethod"} />:{" "}
              {order.paymentMethod}
            </Body2>
          </Grid.Row>

          <Grid.Row>
            <Message info={order.isPaid} warning={!order.isPaid}>
              <FormattedMessage
                id={
                  order.isPaid
                    ? "order.summary.isPaid"
                    : "order.summary.isNotPaid"
                }
              />
              :{" "}
              {order.isPaid &&
                dayjs(order.paidAt).format("MMMM D, YYYY h:mm A")}
            </Message>
          </Grid.Row>

          <Grid.Row className={styles.row}>
            <Heading5>
              <FormattedMessage id="order.summary.orderHeading" />
            </Heading5>
            <List divided relaxed>
              {order.orderItems.map((item: CartData) => (
                <OrderItem
                  key={`orderlist-${item.id}-${item.selectedSize || ""}`}
                  item={item}
                  orderBy={order?.user}
                />
              ))}
            </List>
          </Grid.Row>
        </Grid.Column>

        <Grid.Column computer={6} mobile={16}>
          {error ? (
            <Message warning>{error}</Message>
          ) : (
            <OrderCard cartLength={order.orderItems.length} />
          )}

          {!order.isPaid && (
            <div className={styles.paymentSection}>
              {loadingPay && <Spinner />}

              {order.paymentMethod === "PayPal" && (
                <>
                  {!sdkReady ? (
                    <Spinner />
                  ) : (
                    <PayPalButtonAny
                      amount={order.totalPrice}
                      onSuccess={successPaymentHandler}
                      onCancel={cancelPaymentHandler}
                      onError={errorPaymentHandler}
                    />
                  )}
                </>
              )}

              {order.paymentMethod === "Stripe" && (
                <PrimaryButton
                  onClick={handleStripeCheckout}
                  text={intl.formatMessage({
                    id: "order.summary.payWithStripe",
                    defaultMessage: "Pay with Stripe",
                  })}
                  className={styles.buttonStripe}
                />
                  
                
              )}
            </div>
          )}
        </Grid.Column>
      </Grid>
    </Container>
  ) : null;
};

export default OrderSummary;